import { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  ScrollView,
  Image,
  StyleSheet,
  Platform,
} from 'react-native';
import { createPortal } from 'react-dom';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { useModalWidth } from '../../pages/AdminPanel/Products/formStyles';

function ReferenceOptionRow({ option, selected, onPress, last }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected }}
      style={({ hovered }) => [
        styles.row,
        !last && styles.rowDivider,
        hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
        selected && { backgroundColor: ADMIN_COLORS.accentSoft },
      ]}
    >
      <Image source={{ uri: option.url }} style={styles.thumbnail} resizeMode="cover" />
      <View style={styles.rowText}>
        <Text style={styles.rowTitle} numberOfLines={2}>
          {option.label}
        </Text>
        <Text style={styles.rowSubtitle} numberOfLines={1}>
          {option.subtitle}
        </Text>
      </View>
      <Ionicons
        name={selected ? 'radio-button-on' : 'radio-button-off'}
        size={20}
        color={selected ? ADMIN_COLORS.accent : ADMIN_COLORS.border}
      />
    </Pressable>
  );
}

function PickerCard({
  title,
  subtitle,
  options,
  selectedId,
  onSelect,
  onDismiss,
  onConfirm,
  confirmLabel,
  isLoading,
  errorMessage,
  modalWidth,
}) {
  const selectedOption = options.find((option) => option.id === selectedId) || null;

  return (
    <View style={[styles.card, { width: modalWidth, maxWidth: 560 }]}>
      <View style={styles.header}>
        <Text style={styles.title}>{title}</Text>
        <Pressable onPress={onDismiss} style={styles.closeBtn} hitSlop={8}>
          <Ionicons name="close" size={22} color={ADMIN_COLORS.muted} />
        </Pressable>
      </View>

      <Text style={styles.subtitle}>{subtitle}</Text>

      <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
        {options.map((option, index) => (
          <ReferenceOptionRow
            key={option.id}
            option={option}
            selected={selectedId === option.id}
            onPress={() => onSelect(option.id)}
            last={index === options.length - 1}
          />
        ))}
      </ScrollView>

      {!!errorMessage && <Text style={[styles.subtitle, { color: ADMIN_COLORS.danger }]}>{errorMessage}</Text>}
      <View style={styles.actions}>
        <Pressable
          onPress={onDismiss}
          disabled={isLoading}
          style={({ hovered }) => [styles.cancelBtn, hovered && { opacity: 0.85 }]}
        >
          <Text style={styles.cancelText}>Cancelar</Text>
        </Pressable>
        <Pressable
          onPress={onConfirm}
          disabled={!selectedOption || isLoading}
          style={({ hovered }) => [
            styles.confirmBtn,
            hovered && { backgroundColor: ADMIN_COLORS.primaryBgHover },
            (!selectedOption || isLoading) && styles.confirmBtnDisabled,
          ]}
        >
          <Text style={styles.confirmText}>{isLoading ? 'Copiando…' : confirmLabel}</Text>
        </Pressable>
      </View>
    </View>
  );
}

export default function CoverAiReferencePickerModal({
  visible,
  title = 'Escolher capa de referência',
  subtitle = 'Selecione qual capa usar como template. O prompt será copiado e a imagem escolhida será baixada para anexar na IA da sua preferência (ChatGPT, Claude ou similar).',
  options = [],
  onConfirm,
  onDismiss,
  confirmLabel = 'Copiar prompt e baixar',
  isLoading = false,
  errorMessage,
}) {
  const modalWidth = useModalWidth();
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (visible && options.length) {
      setSelectedId(options[0].id);
    }
  }, [visible, options]);

  const selectedOption = options.find((option) => option.id === selectedId) || null;

  const handleConfirm = () => {
    if (!selectedOption || isLoading) return;
    onConfirm?.(selectedOption);
  };

  if (!visible) return null;

  const card = (
    <PickerCard
      title={title}
      subtitle={subtitle}
      options={options}
      selectedId={selectedId}
      onSelect={setSelectedId}
      onDismiss={onDismiss}
      onConfirm={handleConfirm}
      confirmLabel={confirmLabel}
      isLoading={isLoading}
      errorMessage={errorMessage}
      modalWidth={modalWidth}
    />
  );

  // Modal dentro de modal (ex.: Editar produto) não abre no web — overlay fixo.
  if (Platform.OS === 'web') {
    const overlay = (
      <View style={styles.webOverlayRoot}>
        <Pressable style={styles.webOverlayBackdrop} onPress={isLoading ? undefined : onDismiss} />
        <View style={styles.webOverlayCenter}>{card}</View>
      </View>
    );

    if (typeof document !== 'undefined') {
      return createPortal(overlay, document.body);
    }

    return overlay;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onDismiss}>
      <View style={styles.overlay}>{card}</View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  webOverlayRoot: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
  },
  webOverlayBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  webOverlayCenter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  card: {
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    maxHeight: '85vh',
    overflow: 'hidden',
    zIndex: 10001,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    gap: 12,
  },
  title: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 16,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
    lineHeight: 22,
  },
  closeBtn: {
    padding: 2,
    cursor: 'pointer',
  },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 12,
  },
  list: {
    // maxHeight é um teto (telas altas); flexShrink deixa a lista encolher
    // abaixo disso em telas baixas (celular deitado, janela curta), para que
    // os botões de ação no rodapé do card nunca fiquem cortados pelo
    // `overflow: hidden` do card.
    maxHeight: 360,
    flexShrink: 1,
    minHeight: 80,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: ADMIN_COLORS.border,
  },
  listContent: {
    paddingVertical: 4,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 10,
    cursor: 'pointer',
  },
  rowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  thumbnail: {
    width: 96,
    height: 54,
    borderRadius: 6,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
    lineHeight: 18,
  },
  rowSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    lineHeight: 16,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    padding: 16,
  },
  cancelBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.secondaryBg,
    cursor: 'pointer',
  },
  cancelText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.secondaryText,
  },
  confirmBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.primaryBg,
    cursor: 'pointer',
  },
  confirmBtnDisabled: {
    opacity: 0.55,
  },
  confirmText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.primaryText,
  },
});
